/**
 * Numbered tour stop — Apple Maps waypoint, Tago colour.
 *
 * Teardrop (disc + stem) so the geographic point stays visible under the pin,
 * white number on coral (terminals) or purple (waypoints). Shared by the
 * native MarkerView, the web react-map-gl Marker, and the stop list; do not
 * fork the glyph.
 */
import { Text, View } from 'react-native'
import { Pressable } from '@/components/ui/Pressable'
import { COLORS, FONTS } from '@/lib/brand'
import { INTERACTION } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import type { TourStopKind } from '@/lib/types/tours.types'
import {
  TOUR_STOP_MARKER_DISC,
  TOUR_STOP_MARKER_STEM,
  tourStopFill,
} from '@/lib/maps/tour-stop-layout'

const STEM_DIAMOND = 10

export function TourStopMarker({
  number,
  kind,
  total,
  accessibilityLabel,
  testID,
  selected = false,
  onPress,
}: {
  number: number
  kind: TourStopKind
  total: number
  accessibilityLabel: string
  testID?: string
  selected?: boolean
  onPress?: () => void
}) {
  const fill =
    tourStopFill(kind, number, total) === 'coral' ? COLORS.coral.DEFAULT : COLORS.purple.DEFAULT
  const radius = TOUR_STOP_MARKER_DISC / 2
  const label = String(number)
  const glyph = (
    <View
      pointerEvents="none"
      style={{
        width: TOUR_STOP_MARKER_DISC,
        alignItems: 'center',
      }}
    >
      <View
        style={{
          zIndex: 1,
          width: TOUR_STOP_MARKER_DISC,
          height: TOUR_STOP_MARKER_DISC,
          borderRadius: radius,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: fill,
          borderWidth: selected ? 3 : 2,
          borderColor: COLORS.white,
          ...WEB_SHADOWS.md,
        }}
      >
        <Text
          numberOfLines={1}
          style={{
            fontFamily: FONTS.body,
            fontSize: 12,
            lineHeight: 16,
            color: COLORS.white,
            textAlign: 'center',
          }}
        >
          {label}
        </Text>
      </View>
      <View
        style={{
          width: STEM_DIAMOND,
          height: STEM_DIAMOND,
          marginTop: -(STEM_DIAMOND - TOUR_STOP_MARKER_STEM),
          backgroundColor: fill,
          borderBottomWidth: 1,
          borderRightWidth: 1,
          borderColor: COLORS.white,
          transform: [{ rotate: '45deg' }],
        }}
      />
    </View>
  )

  if (!onPress) {
    return (
      <View
        testID={testID}
        accessibilityRole="image"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ selected }}
        pointerEvents="none"
      >
        {glyph}
      </View>
    )
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected }}
      testID={testID}
      hitSlop={INTERACTION.iconHitSlop}
      style={{
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      {glyph}
    </Pressable>
  )
}
